import { Controller } from "@hotwired/stimulus"
import L from "leaflet/dist/leaflet.js" // UMD minifié : expose aussi window.L (requis par markercluster)

const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png"
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
const NANCY = { center: [48.6921, 6.1844], zoomDesktop: 12, zoomMobile: 11, minZoom: 10, fitMaxZoom: 15 }
const SVG = (inner, size = 18) => `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`
const HOUSE = SVG('<path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 10v9.5h13V10"/><path d="M10 19.5v-5h4v5"/>')
const CALENDAR = SVG('<rect x="3.5" y="5" width="17" height="15.5" rx="2.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/>')
const TARGET = SVG('<circle cx="12" cy="12" r="3.2"/><path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4"/>', 18)
const STAR = '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true"><path d="m12 2.8 2.7 5.8 6.3.7-4.7 4.3 1.3 6.2L12 16.6l-5.6 3.2 1.3-6.2L3 9.3l6.3-.7z"/></svg>'

const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`

// Carte Leaflet des résidences et des événements du Grand Nancy.
// Initialisée à l'approche de la fenêtre (IntersectionObserver).
export default class extends Controller {
  static targets = ["map", "hint", "card", "eventCard", "eventsEmpty", "filter", "status", "toggle", "count"]
  static values = { residences: Array, events: Array, focus: String, clusterUrl: String }

  connect() {
    this.markers = new Map() // clés : "r:<slug>" (résidence), "e:<id>" (événement)
    this.city = ""
    this.show = { residences: true, events: true }
    this.wheelOn = false
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.coarse = window.matchMedia("(pointer: coarse)").matches
    this.teardown = () => this.destroy()
    document.addEventListener("turbo:before-cache", this.teardown)

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.init()
    }, { rootMargin: "300px" })
    this.observer.observe(this.mapTarget)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.teardown)
    this.destroy()
  }

  destroy() {
    this.observer?.disconnect()
    this.resizer?.disconnect()
    clearTimeout(this.hintTimer)
    clearTimeout(this.hoverTimer)
    if (this.map) this.map.remove()
    this.map = null
    this.starting = null
    this.markers = new Map()
    this.mapTarget.replaceChildren()
  }

  // ---------- Initialisation ----------

  init() {
    this.observer?.disconnect()
    this.starting ||= this.build()
    return this.starting
  }

  async build() {
    const residences = this.residencesValue
    const clustered = residences.length >= 8 && this.hasClusterUrlValue
    if (clustered) await this.loadScript(this.clusterUrlValue).catch(() => {})
    if (!this.element.isConnected) return

    this.mapTarget.replaceChildren()
    const map = L.map(this.mapTarget, {
      scrollWheelZoom: false, // activé au clic (ordinateur)
      dragging: !this.coarse, // tactile : un doigt fait défiler la page, deux doigts déplacent la carte
      zoomSnap: 0.5,
      minZoom: NANCY.minZoom,
      worldCopyJump: false
    })
    this.map = map
    map.attributionControl.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noopener">Leaflet</a>')
    L.tileLayer(TILES, { maxZoom: 18, attribution: ATTRIBUTION }).addTo(map)
    map.setView(NANCY.center, this.nancyZoom(), { animate: false })

    this.layer = clustered && L.markerClusterGroup
      ? L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 50, iconCreateFunction: (c) => this.clusterIcon(c) })
      : L.layerGroup()
    this.layer.addTo(map)
    this.eventLayer = L.layerGroup().addTo(map)

    residences.forEach((r) => this.markers.set(`r:${r.slug}`, this.residenceMarker(r)))
    this.eventsValue.forEach((e) => this.markers.set(`e:${e.id}`, this.eventMarker(e)))
    if (!this.hasFocusValue) this.addRecenterControl(map)
    this.refresh({ fit: true })
    this.bindGestures(map)

    this.resizer = new ResizeObserver(() => this.map?.invalidateSize())
    this.resizer.observe(this.mapTarget)
  }

  nancyZoom() {
    return window.matchMedia("(max-width: 767px)").matches ? NANCY.zoomMobile : NANCY.zoomDesktop
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = src
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  addRecenterControl(map) {
    const controller = this
    const Recenter = L.Control.extend({
      options: { position: "topleft" },
      onAdd() {
        const bar = L.DomUtil.create("div", "leaflet-bar carte-recenter")
        const link = L.DomUtil.create("a", "", bar)
        link.href = "#"
        link.role = "button"
        link.title = "Recentrer sur le Grand Nancy"
        link.setAttribute("aria-label", "Recentrer sur le Grand Nancy")
        link.innerHTML = TARGET
        L.DomEvent.disableClickPropagation(bar)
        L.DomEvent.on(link, "click", (e) => { L.DomEvent.preventDefault(e); controller.recenter() })
        return bar
      }
    })
    new Recenter().addTo(map)
  }

  recenter() {
    this.applyCity("")
    this.map?.closePopup()
    this.refresh({ fit: true })
  }

  // ---------- Marqueurs ----------

  residenceMarker(r) {
    const size = r.current ? 56 : 44 // zone cliquable ; la pastille visible fait 36 px (48 px pour la résidence de l'habitant)
    const icon = L.divIcon({
      className: "carte-icon",
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -(size / 2) + 4],
      html: r.current
        ? `<span class="carte-pin carte-pin--current">${HOUSE}<span class="carte-star">${STAR}</span></span>`
        : `<span class="carte-pin">${HOUSE}</span>`
    })
    const marker = L.marker([r.lat, r.lng], { icon, title: r.name, alt: r.name, riseOnHover: true, zIndexOffset: r.current ? 1000 : 500 })
    this.bindPopup(marker, () => this.residencePopup(r), `r:${r.slug}`)
    return marker
  }

  eventMarker(e) {
    const icon = L.divIcon({
      className: "carte-icon",
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -18],
      html: `<span class="carte-pin carte-pin--event">${CALENDAR}</span>`
    })
    const marker = L.marker([e.lat, e.lng], { icon, title: `${e.title} — ${e.when}`, alt: e.title, riseOnHover: true })
    this.bindPopup(marker, () => this.eventPopup(e), `e:${e.id}`)
    return marker
  }

  bindPopup(marker, html, key) {
    if (this.hasFocusValue) return
    marker.bindPopup(html, { maxWidth: 278, minWidth: 240, className: "carte-popup", autoPanPadding: [24, 24] })
    marker.on("popupopen", () => this.highlight(key))
    marker.on("popupclose", () => this.highlight(null))
  }

  clusterIcon(cluster) {
    return L.divIcon({ className: "carte-icon", iconSize: [44, 44], html: `<span class="carte-pin carte-pin--cluster">${cluster.getChildCount()}</span>` })
  }

  popupFrame(photo, inner) {
    const img = photo ? `<img class="carte-popup__photo" src="${esc(photo)}" alt="" width="280" height="112" loading="lazy">` : ""
    return `<div class="carte-popup__body">${img}<div class="carte-popup__text">${inner}</div></div>`
  }

  residencePopup(r) {
    return this.popupFrame(r.photo, `
      <p class="carte-popup__city">${esc(r.city)}</p>
      <p class="carte-popup__name">${esc(r.name)}</p>
      <p class="carte-popup__meta">${plural(r.housing, "logement", "logements")} · ${plural(r.events, "événement à venir", "événements à venir")} · ${plural(r.ideas, "idée", "idées")}</p>
      <a class="carte-popup__link" href="${esc(r.url)}">Voir la résidence</a>`)
  }

  eventPopup(e) {
    const note = e.note ? `<p class="carte-popup__meta">${esc(e.note)}</p>` : ""
    return this.popupFrame(e.photo, `
      <p class="carte-popup__city carte-popup__city--event">Événement</p>
      <p class="carte-popup__name">${esc(e.title)}</p>
      <p class="carte-popup__when">${CALENDAR.replace('width="18" height="18"', 'width="16" height="16"')}<span>${esc(e.when)}</span></p>
      <p class="carte-popup__meta">${esc(e.place)}</p>${note}
      <a class="carte-popup__link carte-popup__link--event" href="${esc(e.url)}">Voir l'événement</a>`)
  }

  // ---------- Affichage, cadrage, filtre ----------

  inCity(item) { return !this.city || item.city === this.city }
  shownResidences() { return this.residencesValue.filter((r) => this.inCity(r)) }
  shownEvents() { return this.eventsValue.filter((e) => this.inCity(e)) }

  refresh({ fit = true } = {}) {
    this.updateCounts()
    if (!this.map) return
    const residences = this.shownResidences()
    const events = this.shownEvents()
    this.layer.clearLayers()
    this.eventLayer.clearLayers()
    if (this.show.residences) residences.forEach((r) => this.layer.addLayer(this.markers.get(`r:${r.slug}`)))
    if (this.show.events) events.forEach((e) => this.eventLayer.addLayer(this.markers.get(`e:${e.id}`)))
    if (fit) this.fit(residences, events)
  }

  fit(residences, events) {
    const opts = { animate: !this.reduced }
    if (!this.city && !this.hasFocusValue) {
      this.map.setView(NANCY.center, this.nancyZoom(), opts) // vue d'ensemble du Grand Nancy
      return
    }
    const points = [...(this.show.residences ? residences : []), ...(this.show.events ? events : [])].map((p) => [p.lat, p.lng])
    if (!points.length) return
    if (points.length === 1) {
      this.map.setView(points[0], this.hasFocusValue ? 15 : 14, opts)
    } else {
      this.map.fitBounds(L.latLngBounds(points), { ...opts, padding: [40, 40], maxZoom: NANCY.fitMaxZoom })
    }
  }

  updateCounts() {
    const n = { residences: this.shownResidences().length, events: this.shownEvents().length }
    this.countTargets.forEach((el) => { el.textContent = n[el.dataset.kind] })
  }

  toggle(event) {
    const button = event.currentTarget
    const kind = button.dataset.kind
    this.show[kind] = !this.show[kind]
    button.setAttribute("aria-pressed", String(this.show[kind]))
    this.map?.closePopup()
    this.refresh({ fit: false })
  }

  filter(event) {
    this.applyCity(event.currentTarget.dataset.city || "")
    this.map?.closePopup()
    this.refresh({ fit: true }) // sans effet tant que la carte n'est pas initialisée : le cadrage se fera à l'init
  }

  applyCity(city) {
    this.city = city
    this.filterTargets.forEach((b) => b.setAttribute("aria-pressed", String((b.dataset.city || "") === city)))
    const slugs = new Set(this.shownResidences().map((r) => r.slug))
    this.cardTargets.forEach((card) => { card.hidden = !slugs.has(card.dataset.slug) })
    const ids = new Set(this.shownEvents().map((e) => String(e.id)))
    this.eventCardTargets.forEach((card) => { card.hidden = !ids.has(card.dataset.eventId) })
    if (this.hasEventsEmptyTarget) this.eventsEmptyTarget.hidden = ids.size > 0
    this.highlight(null)
    this.updateCounts()
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = `${plural(slugs.size, "résidence affichée", "résidences affichées")} et ${plural(ids.size, "événement affiché", "événements affichés")}${city ? ` à ${city}` : ""}.`
    }
  }

  // ---------- Synchronisation liste ↔ carte ----------

  keyOf(card) {
    return card.dataset.eventId ? `e:${card.dataset.eventId}` : `r:${card.dataset.slug}`
  }

  hover(event) {
    if (!window.matchMedia("(hover: hover)").matches) return
    const key = this.keyOf(event.currentTarget)
    clearTimeout(this.hoverTimer)
    this.hoverTimer = setTimeout(() => this.focusMarker(key), 180)
  }

  leave() { clearTimeout(this.hoverTimer) }

  select(event) {
    if (event.target.closest("a")) return // les liens des cards naviguent normalement
    this.focusMarker(this.keyOf(event.currentTarget))
  }

  async focusMarker(key) {
    await this.init()
    const marker = this.markers.get(key)
    if (!this.map || !marker) return
    const kind = key.startsWith("e:") ? "events" : "residences"
    if (!this.show[kind]) { // l'interrupteur de la légende masquait ce marqueur : on le réactive
      this.show[kind] = true
      this.toggleTargets.filter((b) => b.dataset.kind === kind).forEach((b) => b.setAttribute("aria-pressed", "true"))
      this.refresh({ fit: false })
    }
    const open = () => { this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 14), { animate: !this.reduced }); marker.openPopup() }
    if (kind === "residences" && this.layer.zoomToShowLayer) this.layer.zoomToShowLayer(marker, open)
    else open()
  }

  highlight(key) {
    const desktop = window.matchMedia("(min-width: 1024px)").matches
    ;[...this.cardTargets, ...this.eventCardTargets].forEach((card) => {
      const on = key !== null && this.keyOf(card) === key
      card.toggleAttribute("data-active", on)
      if (on && desktop) card.scrollIntoView({ block: "nearest", behavior: this.reduced ? "auto" : "smooth" })
    })
  }

  // ---------- Gestes : la carte ne doit pas piéger le défilement de la page ----------

  bindGestures(map) {
    const el = map.getContainer()
    if (this.coarse) {
      el.addEventListener("touchmove", (e) => { if (e.touches.length === 1) this.showHint("Utilisez deux doigts pour déplacer la carte") }, { passive: true })
      return
    }
    map.on("click", () => { if (!this.wheelOn) { map.scrollWheelZoom.enable(); this.wheelOn = true; this.showHint("") } })
    el.addEventListener("mouseleave", () => { map.scrollWheelZoom.disable(); this.wheelOn = false })
    el.addEventListener("wheel", () => { if (!this.wheelOn) this.showHint("Cliquez sur la carte pour zoomer à la molette") }, { passive: true })
  }

  showHint(text) {
    if (!this.hasHintTarget) return
    clearTimeout(this.hintTimer)
    this.hintTarget.textContent = text
    this.hintTarget.dataset.visible = text ? "true" : "false"
    if (text) this.hintTimer = setTimeout(() => { this.hintTarget.dataset.visible = "false" }, 1600)
  }
}
